import { useCanAccess } from 'ra-core';
import { List } from '@/components/admin/list';
import { DataTable } from '@/components/admin/data-table';
import { SearchInput } from '@/components/admin/search-input';
import { CreateButton } from '@/components/admin/create-button';
import { FilterButton } from '@/components/admin/filter-form';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useStructureCourante } from '../../fournisseurs/structure-courante';
import { Presence } from './Presence';

/** Les huit champs que F-59 enumere, et le contrat en rend exactement huit. */
type Compte = {
  id: string;
  nom: string | null;
  prenom: string | null;
  email: string | null;
  role: string;
  identifiantExterne: string | null;
  etatDuCompte: string;
  rattacheLe: string;
  presence: 'absent' | 'connecte' | 'occupe';
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LES ACTIONS DE TÊTE DE LISTE, ÉCRITES PARCE QUE `hasCreate` NE CONSULTE AUCUN DROIT.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * La liste par défaut de la peau rend `{filtre} {CreateButton si hasCreate} {ExportButton}`, et
 * `Resource.registerResource` de `ra-core` calcule `hasCreate: !!create` — **sans jamais
 * appeler `canAccess`**. Depuis que la ressource porte un écran de création, le bouton
 * paraîtrait donc à l'`orgadmin`, à qui §6.14 répond « **NON** » sur `compte | Créer` et à qui
 * le `@Allow()` de la route répond `403`. F-55 : « **une absence et un refus se lisent
 * pareil** » — un bouton qui mène à un refus est cette confusion, servie à la personne qui
 * administre.
 *
 * **Et l'export part**, ce que `exporter={false}` disait déjà sans l'obtenir : la peau monte
 * `<ExportButton />` **inconditionnellement** dans ses actions par défaut, et `exporter` à
 * `false` ne fait que rendre son clic inerte. F-66 : « **deux exports, et deux seulement** », et
 * ils vivent sur la fiche de la structure. Un bouton qui ne fait rien est le même défaut que
 * ci-dessus, dans l'autre sens.
 */
function ActionsDeLaListe() {
  const { canAccess: peutCreer } = useCanAccess({
    resource: 'comptes',
    action: 'create',
  });
  return (
    <div className="flex items-center gap-2">
      <FilterButton />
      {peutCreer && <CreateButton label="Créer un compte" />}
    </div>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LA LISTE DES COMPTES RATTACHÉS — F-59, ET « LA LISTE EST L'ÉCRAN ».**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * F-59 : « **la liste des comptes rattachés EST l'écran**, ordonnée **par date de
 * rattachement, du plus récent au plus ancien**, ordre unique et non réglable. »
 *
 * **Cet ordre n'est pas un défaut d'affichage, c'est un mécanisme**, et F-59 en donne le
 * motif : « **c'est cet ordre qui remplace l'alerte email supprimée en Q114** : on voit les
 * comptes qui viennent d'être créés **en ouvrant la liste**, pas en recevant un email.
 * **Aucun objet de signalement, aucun badge, aucun compteur de nouveautés** — patron exact de
 * `CDC-4`. »
 *
 * D'où : **aucun tri réglable** — les colonnes sont `disableSort`, et le contrat ne porte
 * aucun paramètre de tri —, et **aucune pastille de nouveauté**. Un tri par nom offert ici
 * casserait le mécanisme sans qu'aucun test ne rougisse.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **HUIT COLONNES, ET F-59 LES ÉNUMÈRE — C'EST SA LISTE, PAS LA MIENNE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * « **Ce que la ligne porte** : nom, prénom, adresse email, rôle, identifiant de la personne
 * chez la structure, état du compte, date du rattachement, **et sa présence en ligne**. »
 * Huit, et le contrat en rend exactement huit.
 *
 * **Ce qu'aucun des deux rôles ne voit d'une personne, écrit en refus explicite** : son
 * **espace de fichiers**, ses **conversations** et tout objet du chat, ses **préférences**. Et
 * « **aucune surface ne montre l'empreinte de mot de passe, ni les codes de vérification, ni
 * les liens d'auto-connexion en cours** ». Le contrat ne les porte pas : il n'y a rien à
 * retirer ici, et c'est le bon sens du montage.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LA RECHERCHE PORTE SUR QUATRE CHAMPS, ET SUR EUX SEULS.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-59 : « **recherche par nom, prénom, adresse email et identifiant chez la structure.** »
 * C'est le serveur qui décide où elle porte — le paramètre est unique et son `.describe()` le
 * dit. Un champ de recherche par colonne donnerait à croire à un filtre par colonne, que le
 * contrat ne porte pas.
 */
export function ListeDesComptes() {
  const { structureId, selecteurAffiche } = useStructureCourante();

  // **F-55 critère 2, servi ici.** « QUAND le compte qui voit tout ouvre un écran de structure
  // **sans structure courante**, LE SYSTÈME DOIT **exiger la sélection d'une structure** au
  // lieu de rendre une liste du parc. » Le fournisseur de données lève dans ce cas ; cet
  // écran dit ce qu'il faut faire plutôt que de laisser voir l'erreur.
  if (structureId === null) {
    return (
      <main className="p-6">
        <Alert>
          <AlertTitle>Choisissez une structure.</AlertTitle>
          <AlertDescription>
            {selecteurAffiche
              ? 'Les comptes se lisent par structure : prenez-en une dans le sélecteur en tête ' +
                'de la surface. Aucune liste du parc entier n’existe ici.'
              : 'Aucun rattachement actif n’est associé à ce compte, donc aucune structure à ' +
                'administrer.'}
          </AlertDescription>
        </Alert>
      </main>
    );
  }

  return (
    <List
      // **La structure courante passe par le filtre**, jamais par un magasin lu dans le
      // fournisseur de données : deux sources pour un périmètre finissent par afficher les
      // comptes d'une autre structure.
      filter={{ structureId }}
      filters={[<SearchInput key="recherche" source="recherche" alwaysOn />]}
      sort={undefined}
      perPage={20}
      // F-66 : « deux exports, et deux seulement ». Ils vivent sur la fiche de la structure.
      exporter={false}
      // **Les actions sont écrites** : voir l'en-tête d'`ActionsDeLaListe`. Le défaut de la peau
      // montrerait le bouton de création à l'`orgadmin` et un bouton d'export inerte aux deux.
      actions={<ActionsDeLaListe />}
    >
      <DataTable>
        <DataTable.Col source="nom" label="Nom" disableSort />
        <DataTable.Col source="prenom" label="Prénom" disableSort />
        <DataTable.Col source="email" label="Adresse" disableSort />
        <DataTable.Col<Compte>
          source="role"
          label="Rôle"
          disableSort
          render={(compte) => <Badge variant="outline">{compte.role}</Badge>}
        />
        <DataTable.Col<Compte>
          source="identifiantExterne"
          label="Identifiant chez la structure"
          disableSort
          // **Vide sur une personne qui administre**, et c'est une contrainte de modèle :
          // Q533, « l'identifiant externe d'un rattachement est facultatif, et **il est vide
          // sur celui d'une personne qui administre** ».
          render={(compte) =>
            compte.identifiantExterne ?? '—'
          }
        />
        <DataTable.Col<Compte>
          source="etatDuCompte"
          label="État"
          disableSort
          render={(compte) =>
            compte.etatDuCompte === 'ANONYMIZED' ? (
              // **« anonymisé » et non « supprimé »** : c'est le seul geste destructif du
              // produit, il est irréversible, et le nommer autrement le rendrait
              // indistinguable d'une suppression.
              <Badge variant="destructive">anonymisé</Badge>
            ) : (
              <Badge variant="secondary">actif</Badge>
            )
          }
        />
        <DataTable.Col<Compte>
          source="rattacheLe"
          label="Rattaché le"
          disableSort
          render={(compte) =>
            new Date(compte.rattacheLe).toLocaleDateString('fr-FR')
          }
        />
        <DataTable.Col<Compte>
          source="presence"
          label="Présence"
          disableSort
          render={(compte) => (
            <Presence etat={compte.presence} />
          )}
        />
      </DataTable>
    </List>
  );
}
